import type { Locale } from "@commerce-frontend/i18n/types";
import { type ArticleCardFragmentFragment, graphql } from "@commerce-frontend/types";
import { Icon } from "@commerce-frontend/ui/base/Icon/Icon";
import {
	Author,
	AuthorAvatar,
	AuthorContent,
	AuthorSubtitle,
	AuthorTitle,
} from "@commerce-frontend/ui/components/Author/Author";
import {
	Card,
	CardContent,
	CardHeading,
	CardHeadline,
	CardMedia,
	CardTitle,
	CardTitleIcon,
	CardTitleWrapper,
} from "@commerce-frontend/ui/components/Card/Card";
import { Link } from "@commerce-frontend/ui/primitives/Link";
import type { HTMLAttributes } from "react";
import { AssetImage } from "~/components/AssetImage/AssetImage";
import { Image, cardImagePlaceholder } from "~/components/Image/Image";

graphql(/* GraphQL */ `
	fragment ArticleCardFragment on ArticleCard {
		id
		title
		location
		publishedAt
		image {
			filename
			alt
		}
		link {
			url
		}
	}
`);

type Props = {
	article: ArticleCardFragmentFragment;
	locale: Locale;
} & HTMLAttributes<HTMLDivElement>;

export const ArticleCard = ({ article, locale }: Props) => {
	const publishedDate = article?.publishedAt && new Date(article.publishedAt);

	return (
		<Link key={article?.id} href={article?.link?.url ?? ""}>
			<Card variant="boxed">
				<CardMedia>
					{article?.image?.filename ? (
						<AssetImage
							data={article?.image}
							style={{ width: "100%", height: "auto" }}
							width="384"
							height="288"
							sizes="(min-width: 0px) 384px"
						/>
					) : (
						<Image
							src={cardImagePlaceholder}
							alt={article?.image?.alt ?? article?.title ?? ""}
							width="384"
							height="288"
							className="w-full"
							sizes="(min-width: 0px) 384px"
						/>
					)}
				</CardMedia>
				<CardContent>
					<CardHeading>
						<CardHeadline>
							<CardTitleWrapper>
								<CardTitle>{article?.title}</CardTitle>
								<CardTitleIcon>
									<Icon icon="arrow-right" />
								</CardTitleIcon>
							</CardTitleWrapper>
						</CardHeadline>
					</CardHeading>
					<Author>
						<AuthorAvatar size="sm">
							<Image alt="Zeeman" src="/_static/icons/avatar.svg" width="40" height="40" />
						</AuthorAvatar>
						<AuthorContent size="sm">
							{article?.location ? <AuthorTitle>{article.location}</AuthorTitle> : null}
							{publishedDate ? (
								<AuthorSubtitle>
									{publishedDate.toLocaleDateString(locale, {
										day: "numeric",
										month: "short",
										year: "numeric",
									})}
								</AuthorSubtitle>
							) : null}
						</AuthorContent>
					</Author>
				</CardContent>
			</Card>
		</Link>
	);
};
